import { useState, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { FIND_LIST_VALUES_OPTIMIZED, FIND_LIST_VALUES_PAGINATED, QUERY_GET_LIST_VALUES } from 'graphql/queries/customLists';
import { FindListValuesOptimized, FindListValuesPaginated, FindListValuesVariablesPaginated } from '../types';
import { FindListValuesVariables, FindListValues, ListDataTypeEnum, ListValue } from 'views/backoffice/CustomLists/types';
import { filterUserEnabledOptions, getFilteredValues, hasReachedLastPage, shouldAutoAdvancePage } from '../utils';

const LIMIT = 10;
export const useLazyListValues = (listId: number, listDatatype: ListDataTypeEnum | undefined, selectedId?: string) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<ListValue[]>([]);
    const [value, setValue] = useState<ListValue | null>(null);
    const [allValues, setAllValues] = useState<ListValue[] | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [pagesFetched, setPagesFetched] = useState<Set<number>>(new Set());
    const [fetchOptimized, { loading: loadingOptimized }] = useLazyQuery<FindListValuesOptimized, FindListValuesVariables>(
        FIND_LIST_VALUES_OPTIMIZED
    );
    const [fetchPaginated, { loading: loadingPaginated }] = useLazyQuery<FindListValuesPaginated, FindListValuesVariablesPaginated>(
        FIND_LIST_VALUES_PAGINATED
    );
    const [findListValue, { loading: loadingSelectedValue }] = useLazyQuery<FindListValues, FindListValuesVariables>(
        QUERY_GET_LIST_VALUES
    );
    const isFirstSearch = useRef(true);
    const lastFetchedSelectedId = useRef<string | undefined>(undefined);
    const optionsRef = useRef<ListValue[]>([]);
    const manuallyAddedIds = useRef<Set<string>>(new Set());
    
    // Keep optionsRef in sync with options
    useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    useEffect(() => {
        if (inputValue.length > 0) {
            if (isFirstSearch.current && !allValues) {
                isFirstSearch.current = false;
                setIsSearching(true);
                fetchOptimized({ variables: { data: { listIds: [listId] } } }).then(({ data }) => {
                    if (data?.findListValuesOptimized) {
                        const optimizedListValues = data.findListValuesOptimized;
                        setAllValues(optimizedListValues);
                        setOptions(filterUserEnabledOptions(optimizedListValues, listDatatype, selectedId));
                    }
                    setIsSearching(false);
                });
            }
            if (allValues) {
                const filteredValues = getFilteredValues(allValues, listDatatype, inputValue);
                setOptions(filterUserEnabledOptions(filteredValues, listDatatype, selectedId));
            }
        }
        if (!inputValue.length && allValues) setOptions(filterUserEnabledOptions(allValues, listDatatype, selectedId));
    }, [inputValue, listId, fetchOptimized, allValues, listDatatype, selectedId]);

    // Load paginated values
    useEffect(() => {
        if (!isSearching && !allValues && open && hasMore && !pagesFetched.has(page) && listId !== -1) {
            fetchPaginated({
                variables: { data: { listIds: [listId] }, pagination: { limit: LIMIT, offset: page } }
            }).then(({ data }) => {
                if (data?.findListValuesPaginated?.listValues) {
                    const newValues = data.findListValuesPaginated.listValues;
                    // Filter out manually added IDs (like selectedId) to prevent duplicates
                    const filteredNewValues = newValues.filter((val) => !manuallyAddedIds.current.has(val.id));
                    const userFiltered = filterUserEnabledOptions(filteredNewValues, listDatatype, selectedId);
                    // optionsRef holds the last committed options; userFiltered is what we are about to append
                    const visibleCount = optionsRef.current.length + userFiltered.length;
                    setOptions((prev) => [...prev, ...userFiltered]);
                    setPagesFetched((prev) => new Set(prev).add(page));
                    // Keep fetching pages when enabled-user filtering leaves the visible list too
                    // short (or empty) to be scrollable; otherwise the scroll-triggered "load more"
                    // never fires and enabled users on later pages stay hidden (OBD-870).
                    if (hasReachedLastPage(page, LIMIT, data.findListValuesPaginated.total)) {
                        setHasMore(false);
                    } else if (
                        shouldAutoAdvancePage({ visibleCount, lastPageAdded: userFiltered.length, rawPageSize: newValues.length })
                    ) {
                        setPage((prev) => prev + 1);
                    }
                }
            });
        }
    }, [page, open, isSearching, allValues, fetchPaginated, hasMore, listId, pagesFetched, listDatatype, selectedId]);

    // Fetch selectedId when provided and not already in options
    useEffect(() => {
        if (selectedId && selectedId !== lastFetchedSelectedId.current) {
            // For base fields (listId === -1), find the value from allValues or options
            if (listId === -1) {
                // Check both allValues and options since allValues might not be set yet
                const availableOptions = allValues || optionsRef.current;
                const foundValue = availableOptions.find((opt) => opt.id === selectedId);
                if (foundValue) {
                    lastFetchedSelectedId.current = selectedId;
                    // Set the value so it displays in the input
                    setValue(foundValue);
                    // Add to options if not already there
                    setOptions((prev) => {
                        const exists = prev.some((opt) => opt.id === foundValue.id);
                        if (!exists) {
                            return [...prev, foundValue];
                        }
                        return prev;
                    });
                }
            } else {
                // For regular lists, fetch from API
                const selectedIdNumber = Number(selectedId);
                if (!isNaN(selectedIdNumber)) {
                    // Check if the selectedId is already in options using ref to avoid dependency issues
                    const isAlreadyInOptions = optionsRef.current.some((opt) => opt.id === selectedId);
                    
                    if (!isAlreadyInOptions) {
                        lastFetchedSelectedId.current = selectedId;
                        findListValue({
                            variables: { data: { ids: [selectedIdNumber] } }
                        }).then(({ data }) => {
                            if (data?.findListValues && data.findListValues.length > 0) {
                                const selectedValue = data.findListValues[0];
                                // Track this ID as manually added to prevent it from being added again by paginated queries
                                manuallyAddedIds.current.add(selectedValue.id);
                                // Add the selected value to options if it's not already there
                                setOptions((prev) => {
                                    const exists = prev.some((opt) => opt.id === selectedValue.id);
                                    if (!exists) {
                                        return [...prev, selectedValue];
                                    }
                                    return prev;
                                });
                                // Set the value so it displays in the input
                                setValue(selectedValue);
                            }
                        });
                    }
                }
            }
        } else if (!selectedId) {
            lastFetchedSelectedId.current = undefined;
        }
    }, [selectedId, findListValue, listId, allValues, setValue, setOptions]);

    return {
        open,
        setOpen,
        inputValue,
        setInputValue,
        options,
        setOptions,
        value,
        setValue,
        allValues,
        isSearching,
        loadingOptimized,
        loadingPaginated: loadingPaginated || loadingSelectedValue,
        page,
        setPage,
        hasMore,
        setAllValues
    };
};
